import { useQuery } from '@tanstack/react-query';

import React, { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { Delete as DeleteIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Box, Breadcrumbs, IconButton, Link, Menu, MenuItem, Paper, Stack, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { ConfirmDialog } from '@@components';

import { ContextClient, ContextModel } from '../../api/api';
import { useDesignerScreenContext } from '../../components/Chat/DesignerScreenContext';
import ContextLinks from './Components/ContextLinks';
import { contextDesignerQueryKeys } from './ContextDesignerQueryKeys';

export default function ContextDetailView() {
	const { id: contextId } = useParams<{ id: string }>();
	useDesignerScreenContext({ ScreenArea: 'Service', ContextId: contextId });
	const client = useApiClient(ContextClient);
	const toast = useToastService();
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const menuOpen = Boolean(menuAnchorEl);

	const { data: context, isLoading } = useQuery<ContextModel>({
		queryKey: contextDesignerQueryKeys.context(contextId ?? ''),
		queryFn: async () => {
			if (!contextId) throw new Error('Context ID is required');
			return client.getContextById(contextId);
		},
		enabled: !!contextId,
	});

	const { data: subDomainsData } = useQuery({
		queryKey: contextDesignerQueryKeys.subDomains(),
		queryFn: async () => client.getSubDomains(),
	});

	// Find the subdomain that contains this context
	const parentSubDomain = subDomainsData?.find((sd) => sd.contexts?.some((c) => c.id === contextId));

	const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setMenuAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
	};

	const handleDeleteClick = () => {
		handleMenuClose();
		setConfirmDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!context?.id) return;

		setIsDeleting(true);
		try {
			await client.delete(context.id);
			setConfirmDialogOpen(false);
			toast.success(`Context "${context.contextName}" deleted successfully`);
			// Navigate back to the service contexts list
			if (parentSubDomain?.subDomain?.id) {
				window.location.href = `/service/${parentSubDomain.subDomain.id}`;
			} else {
				window.location.href = '/service';
			}
		} catch (error) {
			toast.error(`Failed to delete context: ${error instanceof Error ? error.message : 'Unknown error'}`);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleDeleteCancel = () => {
		setConfirmDialogOpen(false);
	};

	if (isLoading) {
		return (
			<Stack direction="column" sx={{ flexGrow: 1, p: 2 }}>
				<Typography>Loading context...</Typography>
			</Stack>
		);
	}

	if (!context) {
		return (
			<Stack direction="column" sx={{ flexGrow: 1, p: 2 }}>
				<Typography color="error">Context not found</Typography>
			</Stack>
		);
	}

	return (
		<Stack direction="column" sx={{ flexGrow: 1, boxShadow: 'none', border: 0 }}>
			<Breadcrumbs sx={{ mb: 2 }}>
				<Link component={RouterLink} to="/service" color="inherit">
					Services
				</Link>
				{parentSubDomain && (
					<Link component={RouterLink} to={`/service/${parentSubDomain.subDomain?.id}`} color="inherit">
						{parentSubDomain.subDomain?.name}
					</Link>
				)}
				<Typography color="text.primary">{context.contextName}</Typography>
			</Breadcrumbs>

			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
				<Typography variant="h4" color="textSecondary">
					Context {context.contextName}
				</Typography>
				<IconButton onClick={handleMenuClick} size="small" aria-label="Context actions" disabled={isDeleting}>
					<MoreVertIcon />
				</IconButton>
			</Box>

			<Menu
				anchorEl={menuAnchorEl}
				open={menuOpen}
				onClose={handleMenuClose}
				MenuListProps={{
					'aria-labelledby': 'context-menu-button',
				}}
			>
				<MenuItem onClick={handleDeleteClick} disabled={isDeleting}>
					<DeleteIcon sx={{ mr: 1 }} fontSize="small" />
					Delete Context
				</MenuItem>
			</Menu>

			<Paper sx={{ flexGrow: 1, flexShrink: 1 }}>
				<Box sx={{ p: 0 }}>
					<ContextLinks contextId={context?.id ?? ''} />
				</Box>
			</Paper>

			<ConfirmDialog
				open={confirmDialogOpen}
				title="Delete Context"
				message={`Are you sure you want to delete the context "${context?.contextName}"? This action cannot be undone.`}
				confirmText="Delete"
				cancelText="Cancel"
				confirmColor="error"
				onConfirm={handleDeleteConfirm}
				onCancel={handleDeleteCancel}
			/>
		</Stack>
	);
}
